import { Box, List, ListItem, ListItemText, Stack, Typography } from '@mui/material';
import section5_image from 'assets/img/ECommerce/section5_image.png';
import React, { JSX, ReactNode } from 'react';
import { E_COMMERCE_STYLES } from './config';

type CaparisonData = {
  number: number;
  title: string;
  subtitle?: string;
  bgColor: string;
  textColor: string;
  listColor: string;
  image?: string;
  helpText?: string;
  list: ReactNode[];
};

export default function Section5(): JSX.Element {
  const caparisonData: CaparisonData[] = [
    {
      number: 1,
      title: 'SaaS Platforms',
      subtitle: '(Shopify, Woo Commerce, etc.)',
      bgColor: '#FCF9EA',
      textColor: '#949494',
      listColor: '#898576',
      image: section5_image,
      list: [
        <>
          {' '}
          <u>Theme-based</u> setup for <u>quick launches.</u>
        </>,
        <>
          {' '}
          <u>Plugin ecosystem </u> adds features, <u>with some limits</u>
        </>,
        <>
          {' '}
          Works <u>best for simple workflows</u>
        </>,
        <>
          {' '}
          Fixed <u>monthly pricing, limited backend control</u>
        </>,
        <>
          {' '}
          May face <u>performance issues at scale</u>
        </>,
        <>
          {' '}
          Build with <u>third-party apps</u>
        </>,
        <>
          {' '}
          <u>Generic UX</u> — may not suit regional behavior
        </>,
        <> Less flexibility for long-term customization</>,
      ],
      helpText: 'This is the first option, but sorry with us!',
    },
    {
      number: 2,
      title: 'Osperb’s Scratch Built E-Commerce',
      subtitle: '',
      bgColor: '#E8F3FF',
      textColor: '#0044FF',
      listColor: '#000000',
      image: '',
      list: [
        <>
          {' '}
          <u>Theme-based</u> to your <u>business flow, ops & branding</u>
        </>,
        <>
          {' '}
          Built with <u>enterprise tech</u> (React, Node, Flutter)
        </>,
        <>
          {' '}
          Scalable and flexible <u>for long-term growth</u>
        </>,
        <>
          {' '}
          Seamless ERP, CRM, and <u>API integrations</u>
        </>,
        <>
          {' '}
          UX designed around <u>customer psychology</u>
        </>,
        <>
          {' '}
          Clean, <u>future-ready codebase</u>
        </>,
        <>
          {' '}
          <u>No dependency</u> on third-party plugins
        </>,
        <>
          {' '}
          <u>Full ownership</u> and control of your platform
        </>,
        <>
          {' '}
          <u>Dedicated dev team & ongoing support</u>
        </>,
        <>
          {' '}
          Arabic RTL, multi-currency,<u>GCC-ready</u>
        </>,
        <>
          {' '}
          Custom <u>admin panel with team training</u>
        </>,
        <>
          {' '}
          <u>One-time build cost </u>no recurring platform fees
        </>,
      ],
      helpText: '',
    },
  ];

  return (
    <Box sx={{ px: 1, py: 6, pt: 8 }}>
      <Stack maxWidth={E_COMMERCE_STYLES.container.maxWidth} mx={E_COMMERCE_STYLES.container.mx}>
        <Stack
          sx={{ marginBottom: '60px' }}
          px={0}
          spacing={0}
          textAlign={'center'}
          maxWidth={E_COMMERCE_STYLES.container.maxWidth}
          mx={E_COMMERCE_STYLES.container.mx}
        >
          <Typography
            variant="body1"
            fontSize={32}
            lineHeight={'40px'}
            color="text.black"
            className="font-helvetica-thin"
          >
            There Are Two Ways to Build an Online Store
          </Typography>
          <Typography
            variant="body1"
            fontSize={32}
            color="text.black"
            className="font-helvetica-bold"
          >
            Choose Wisely.
          </Typography>
        </Stack>

        <Box
          sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr' }, gap: 0.6 }}
        >
          {caparisonData?.map((item, key) => (
            <Box
              sx={{
                bgcolor: item.bgColor,
                p: 2,
                borderRadius: '9px',
                position: 'relative',
                minHeight: '200px',
              }}
              key={key}
            >
              {/* Number Circle */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '-25px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  bgcolor: 'white',
                  borderRadius: '50%',
                  fontSize: 18,
                  color: item.textColor,
                }}
              >
                <Box
                  className="font-helvetica-light"
                  sx={{
                    backgroundColor: item.bgColor,
                    width: 40,
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 600,
                    borderRadius: '50%',
                  }}
                >
                  {item.number}
                </Box>
              </Box>

              <Stack sx={{ width: '100%' }}>
                <Box mb={key === 0 ? 4 : 2}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: item.textColor,
                      mb: 0,
                      mt: 2,
                    }}
                    className="font-helvetica-bold"
                  >
                    {item.title}
                  </Typography>
                  {item.subtitle && (
                    <Typography
                      variant="body2"
                      className="font-helvetica"
                      sx={{ color: '#949494', lineHeight: '17px' }}
                    >
                      {item.subtitle}
                    </Typography>
                  )}
                </Box>

                {item.image && (
                  <Box
                    component="img"
                    src={item.image}
                    alt="hero"
                    sx={{
                      width: '100%',
                      borderRadius: 2,
                      zIndex: 2,
                      px: 1,
                      mb: 2,
                    }}
                  />
                )}

                <Box>
                  <List
                    dense
                    disablePadding
                    sx={{
                      listStyleType: 'disc',
                      pl: 2,
                      color: item?.listColor,
                    }}
                  >
                    {item?.list?.map((list, listKey) => (
                      <ListItem
                        dense
                        disableGutters
                        sx={{ display: 'list-item', px: 0, py: 0 }}
                        key={listKey}
                      >
                        <ListItemText
                          primary={
                            <Typography
                              lineHeight={'18px'}
                              variant="body2"
                              className="font-helvetica-light"
                            >
                              {list}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
                <Typography variant="caption" color="#9C9C9C" my={1}>
                  <i>{item?.helpText}</i>
                </Typography>
              </Stack>
            </Box>
          ))}
        </Box>
      </Stack>
    </Box>
  );
}
